import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import VendorDetailPanel from '@/pages/vendors/VendorDetailPanel'
import { AlertTriangle, ClipboardList, MoreHorizontal, UserMinus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useHipaaEnabled } from '@/hooks/useComplianceFramework'
import { Button } from '@deepguide-ai/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@deepguide-ai/ui/dropdown-menu'
import { DetailPanelSkeleton } from '@/components/compliance/detail-primitives'
import { VeraActionBanner } from '@/components/compliance/VeraActionBanner'
import {
  PageHeaderActions,
  PageHeaderBreadcrumb,
} from '@/components/layout/PageHeaderActions'
import { usePageHeaderTitle } from '@/lib/page-header'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/vendors_/$vendorId'
)({
  validateSearch: (search: Record<string, unknown>) => ({
    // Assessment stages stay on this page; tabs hold supporting records.
    tab:
      typeof search.tab === 'string' &&
      ['risks', 'reviews', 'documents'].includes(search.tab)
        ? search.tab
        : ['overview', 'details'].includes(String(search.tab))
          ? 'risks'
          : undefined,
  }),
  component: VendorDetailPage,
  staticData: { pageHeader: { title: 'Vendor Detail', pad: false, scroll: false } },
})

function VendorDetailPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const hipaaEnabled = useHipaaEnabled()
  const deleteMutation = useMutation(
    trpc.vendorManagement.delete.mutationOptions()
  )
  const [showOffboardDialog, setShowOffboardDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const permanentDeleteMutation = useMutation(trpc.vendorManagement.deletePermanently.mutationOptions())
  const [startedAssessmentId, setStartedAssessmentId] = useState<string | null>(null)
  const assessMutation = useMutation(trpc.vendorAssessment.assess.mutationOptions())
  const initiateMutation = useMutation(
    trpc.vendorAssessment.initiate.mutationOptions()
  )
  const { workspaceId, vendorId } = Route.useParams()
  const { tab } = Route.useSearch()
  const { canWorkspaceWrite, canDelete } = useWorkspacePermissions(workspaceId)

  const detailQuery = useQuery(
    trpc.vendorManagement.getById.queryOptions(
      { id: vendorId ?? '' },
      { enabled: Boolean(vendorId) }
    )
  )
  const item = detailQuery.data ?? null

  const goBackToVendors = () => {
    navigate({
      to: '/workspaces/$workspaceId/vendors',
      params: { workspaceId },
    })
  }

  const assessmentsQuery = useQuery({
    ...trpc.vendorAssessment.listByVendor.queryOptions({ vendorId: vendorId ?? '' }),
    enabled: Boolean(vendorId),
  })
  const approvedAssessment = [...(assessmentsQuery.data ?? [])].filter(a => a.status === 'APPROVED').sort((a, b) => b.version - a.version)[0]
  const resumableAssessment = [...(assessmentsQuery.data ?? [])]
    .filter((a: any) => ['DRAFT', 'NEEDS_REVISION', 'SUBMITTED'].includes(a.status))
    .sort((a: any, b: any) => b.version - a.version)[0] as any | undefined

  const handleAssess = async () => {
    if (!canWorkspaceWrite || item?.status === 'OFFBOARDED') return
    if (resumableAssessment) return
    try {
      const created = await initiateMutation.mutateAsync({ vendorId })
      const result = await assessMutation.mutateAsync({ assessmentId: created.id })
      const failed = result.perDocument.filter(document => !document.ok).length
      if (failed) toast.warning(`${failed} document(s) could not be read. Review the assessment for gaps.`)
      setStartedAssessmentId(created.id)
      invalidateAll()
    } catch (err) {
      console.error('[VendorDetailPage] start assessment failed', err)
      invalidateAll()
      toast.error(err instanceof Error ? err.message : 'Could not start the assessment')
    }
  }

  const invalidateAll = (includeDetail = true) => {
    queryClient.invalidateQueries({
      queryKey: trpc.vendorManagement.list.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vendorManagement.listOptions.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vendorManagement.getStats.queryKey(),
    })
    queryClient.invalidateQueries({ queryKey: trpc.vendorManagement.getDistribution.queryKey() })
    if (hipaaEnabled) {
      queryClient.invalidateQueries({
        queryKey: trpc.vendorManagement.getBaaStats.queryKey(),
      })
    }
    if (vendorId && includeDetail) {
      queryClient.invalidateQueries({ queryKey: trpc.vendorAssessment.listByVendor.queryKey({ vendorId }) })
      queryClient.invalidateQueries({
        queryKey: trpc.vendorManagement.getById.queryKey({ id: vendorId }),
      })
    }
  }

  const handleOffboard = async () => {
    if (!canWorkspaceWrite || !item) return
    try {
      await deleteMutation.mutateAsync({ id: item.id })
      invalidateAll()
      setShowOffboardDialog(false)
      goBackToVendors()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not offboard vendor')
    }
  }

  const handleDelete = async () => {
    if (!canDelete || !item) return
    try {
      await permanentDeleteMutation.mutateAsync({ id: item.id })
      invalidateAll(false)
      setShowDeleteDialog(false)
      goBackToVendors()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete vendor')
    }
  }

  usePageHeaderTitle(item ? `${item.displayId}: ${item.name}` : undefined)

  if (detailQuery.isLoading && !detailQuery.data) {
    return (
      <div className='flex h-[calc(var(--app-vh)_-_50px)] flex-col overflow-hidden'>
        <DetailPanelSkeleton />
      </div>
    )
  }

  if (!vendorId || detailQuery.isError || !item) {
    return (
      <div className='flex h-[calc(var(--app-vh)_-_50px)] flex-col overflow-hidden px-6 py-6'>
        <div className='rounded-sm border border-dashed border-border-divider bg-card px-5 py-10 text-center'>
          <AlertTriangle className='mx-auto mb-3 h-8 w-8 text-text-disabled' />
          <p className='font-body text-[13px] font-medium text-text-body'>
            Vendor not found
          </p>
          <p className='font-body mt-1 text-[12px] text-muted-foreground'>
            This vendor may have been deleted or is no longer available.
          </p>
        </div>
      </div>
    )
  }

  const assessmentAction = (
    <Button
      size='sm'
      className='h-8 shrink-0 rounded-sm px-3 text-[12px]'
      onClick={() => void handleAssess()}
      disabled={initiateMutation.isPending || assessMutation.isPending || assessmentsQuery.isLoading || assessmentsQuery.isError}
    >
      <ClipboardList className='h-3.5 w-3.5' />
      {initiateMutation.isPending || assessMutation.isPending
        ? 'Assessing'
        : approvedAssessment ? 'Start reassessment' : 'Start Assessment'}
    </Button>
  )

  return (
    <div className='flex h-[calc(var(--app-vh)_-_50px)] flex-col overflow-hidden'>
      <PageHeaderBreadcrumb>
        <button
          type='button'
          onClick={goBackToVendors}
          className='font-heading text-[15px] font-medium text-text-subtle hover:text-cobalt-strong'
        >
          Vendors
        </button>
      </PageHeaderBreadcrumb>
      {canWorkspaceWrite && (
        <PageHeaderActions>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant='outline' size='smIcon' aria-label='More vendor actions'>
                <MoreHorizontal className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onSelect={() => setShowOffboardDialog(true)} disabled={deleteMutation.isPending || permanentDeleteMutation.isPending || item.status === 'OFFBOARDED'}>
                <UserMinus className='size-4' />Offboard Vendor
              </DropdownMenuItem>
              {canDelete && <DropdownMenuItem onSelect={() => setShowDeleteDialog(true)} disabled={deleteMutation.isPending || permanentDeleteMutation.isPending}>
                <Trash2 className='size-4' />Delete Vendor
              </DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </PageHeaderActions>
      )}
      <VendorDetailPanel
        item={item}
        startedAssessmentId={startedAssessmentId}
        onUpdate={invalidateAll}
        readOnly={!canWorkspaceWrite}
        tab={tab ?? 'risks'}
        reviewBanner={
          item.status !== 'OFFBOARDED' && canWorkspaceWrite && !resumableAssessment ? (
            approvedAssessment ? <VeraActionBanner
              title={`Assessment v${approvedAssessment.version} is approved`}
              description='The latest approved result stays in effect while reassessment changes are reviewed.'
              action={assessmentAction}
            /> : <div className='flex justify-end'>{assessmentAction}</div>
          ) : null
        }
        onTabChange={(next) =>
          navigate({
            to: '/workspaces/$workspaceId/vendors/$vendorId',
            params: { workspaceId, vendorId },
            search: { tab: next },
            replace: true,
          })
        }
      />
      <ConfirmDialog
        open={showOffboardDialog}
        onOpenChange={setShowOffboardDialog}
        title='Offboard Vendor'
        desc={`Are you sure you want to offboard "${item.displayId}"? Their status will be set to Offboarded.`}
        destructive
        confirmText={deleteMutation.isPending ? 'Offboarding...' : 'Offboard'}
        isLoading={deleteMutation.isPending}
        handleConfirm={() => {
          void handleOffboard()
        }}
      />
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title='Delete vendor permanently?'
        desc={`Delete "${item.displayId}" and its assessments and vendor links? Linked risks, assets and independently stored evidence remain. This action cannot be undone.`}
        destructive
        confirmText={permanentDeleteMutation.isPending ? 'Deleting...' : 'Delete permanently'}
        isLoading={permanentDeleteMutation.isPending}
        handleConfirm={() => { void handleDelete() }}
      />
    </div>
  )
}
